import { EmailReportsToggle } from '@va/dashboard/feature/general-settings';
import { NewsletterToggle } from '@va/dashboard/feature/newsletter';
import { FooterButtons } from '@va/dashboard/shared/modals/AnnouncementModal/AnnouncementModalFooterButtons';
import { useTranslate } from '@va/localization';
import { AnnouncementContentType } from '@va/types/announcements';
import { useWindowDimensions } from '@va/util/hooks';
import { Link } from '@va/util/router';
import classNames from 'classnames';
import Checkbox from 'common/components/misc/Checkbox';
import CopyableBox from 'common/components/misc/CopyableBox';
import parse, { DOMNode, domToReact, Element, HTMLReactParserOptions } from 'html-react-parser';
import { useMemo } from 'react';
import './AnnouncementModal.scss';

type AnnouncementModalProps = {
  onClose: () => void;
  content: AnnouncementContentType;
  isChecked: boolean;
  onCheckboxChange: () => void;
  modalId: string;
};

const AnnouncementModalV2 = ({ onClose, content, isChecked, onCheckboxChange, modalId }: AnnouncementModalProps) => {
  const translate = useTranslate();
  const { height, width } = useWindowDimensions();

  const shouldSplitInTwoParts = useMemo(() => {
    return (height <= 640 && width > 1100) || width > height;
  }, [height, width]);

  const htmlParseOptions: HTMLReactParserOptions = {
    replace: (domNode) => {
      const elementDomNode = domNode as Element;
      if (elementDomNode.type === 'tag' && elementDomNode.name === 'internal-link') {
        return <Link to={elementDomNode.attribs.to}>{domToReact(elementDomNode.children as DOMNode[])}</Link>;
      }
    },
  };

  const showVideo = useMemo(() => content.video?.url, [content.video?.url]);

  const videoOrImageContent = useMemo(() => {
    return (
      <>
        {showVideo && (
          <div className='flex h-full aspect-video'>
            <video controls src={content.video?.url} poster={content.video?.poster} className='min-w-full bg-black'>
              Sorry, your browser doesn't support embedded videos, but don't worry, you can
              <a href={content.video?.url}>download it</a>
              and watch it with your favorite video player!
            </video>
          </div>
        )}
        {!showVideo && content.image && (
          <ModalImage
            imageUrl={content.image}
            targetUrl={content.target_url}
            className={
              !shouldSplitInTwoParts
                ? 'h-full aspect-square rounded-12 overflow-hidden max-w-[21.875rem] mx-auto min-h-200'
                : 'h-full aspect-square min-h-200 rounded-24 overflow-hidden'
            }
          />
        )}
      </>
    );
  }, [content.image, content.target_url, content.video?.poster, content.video?.url, shouldSplitInTwoParts, showVideo]);

  const showNewsletterToggle = useMemo(() => content.show_newsletter_toggle, [content.show_newsletter_toggle]);
  const showEmailReportsToggle = useMemo(() => content.show_email_reports_toggle, [content.show_email_reports_toggle]);

  return (
    <div
      className={classNames('flex rounded-24', {
        'flex-row h-[22.5rem] gap-17u': shouldSplitInTwoParts,
        'max-w-[84.375rem]': shouldSplitInTwoParts && showVideo,
        'max-w-[52.5rem]': shouldSplitInTwoParts && !showVideo,
        'flex pl-6 flex-col max-w-[33.75rem]': !shouldSplitInTwoParts,
      })}
    >
      {shouldSplitInTwoParts && <div className='flex h-full justify-center items-center'>{videoOrImageContent}</div>}

      <div className='flex flex-col overflow-hidden'>
        <div className={classNames('flex flex-col flex-1 pr-17u', { 'overflow-hidden': shouldSplitInTwoParts })}>
          <div
            className={classNames('sticky top-0 bg-white z-40 w-full mb-8u pt-10u min-h-40u flex', {
              'min-h-fit': content.headline?.trim()?.length,
            })}
          >
            <span className={'font-semi-bold text-display-large leading-display-large tracking-display-large'}>
              {content.headline && parse(content.headline, htmlParseOptions)}
            </span>
          </div>
          {!shouldSplitInTwoParts && videoOrImageContent}
          {(showEmailReportsToggle || showNewsletterToggle) && (
            <div className={classNames('flex flex-col gap-1u mb-8u', { 'mt-6': !shouldSplitInTwoParts })}>
              {showEmailReportsToggle && (
                <EmailReportsToggle className={classNames({ 'rounded-b-none': showNewsletterToggle })} size='small' />
              )}
              {showNewsletterToggle && (
                <NewsletterToggle className={classNames({ 'rounded-t-none': showEmailReportsToggle })} size='small' />
              )}
            </div>
          )}
          <div
            className={classNames('flex flex-col gap-4u', {
              'overflow-y-scroll scrollbar scrollbar-thumb': shouldSplitInTwoParts,
            })}
          >
            <div
              className={'announcement-modal-text font-medium text-body-small leading-body-small tracking-body-small'}
            >
              {parse(content.text, htmlParseOptions)}
            </div>
            {content.voucher_code && (
              <div className='w-4/5 xs:w-full mx-auto my-0 mt-2'>
                <CopyableBox text={content.voucher_code} />
              </div>
            )}
            {content.confirmation_needed && (
              <Checkbox
                id='read-id'
                label={content.confirmation_text || translate('modal.announcement.understood')}
                isChecked={isChecked}
                onCheckboxChange={onCheckboxChange}
              />
            )}
          </div>
        </div>
        <FooterButtons content={content} onClose={onClose} isChecked={isChecked} modalId={modalId} />
      </div>
    </div>
  );
};

export default AnnouncementModalV2;

const ModalImage = ({
  imageUrl,
  targetUrl,
  className,
}: {
  imageUrl: string;
  targetUrl: string;
  className?: string;
}) => {
  if (!imageUrl) return null;
  const imgContent = <img src={imageUrl} alt={'announcement-img'} className='h-full w-full object-cover' />;
  if (targetUrl) {
    return (
      <a target='_blank' rel='noreferrer' href={targetUrl} className={className}>
        {imgContent}
      </a>
    );
  } else {
    return <div className={className}>{imgContent}</div>;
  }
};
